import { assertServiceHost, axiosClient } from '@nx-frontend/shared/api';
import { ServicePayload, UserServiceAvatarDto, UserServiceUserDto, UserVar } from '@nx-frontend/shared/types';

export enum USER_VARIABLE {
  IGNORE_INTRO = 'ignoreIntro',
  NOTE_APPEARANCE = 'noteAppearance',
  NOTE_FULL_WIDTH = 'noteFullWidth',
  IGNORE_SHARES_INFO = 'ignoreSharesInfo',
  IGNORE_START_TOOLTIPS = 'ignoreStartTooltips',
  COLOR_THEME = 'colorTheme',
  HELP_TOOLTIPS_START_SHOWN = 'helpTooltipsStartShown',
  HELP_TOOLTIPS_NOTE_SHOWN = 'helpTooltipsNoteShown',
  DESKTOP_APPS_INFORMER_SHOWN = 'desktopAppsInformerShown',
  LAST_OPENED_WORKSPACES = 'lastOpenedWorkspaces',
  IS_OCR_ADVERT_SHOWN = 'isOcrAdvertShown',
  PROFESSION_ASK_SHOWN = 'professionAskShown',
  GOOGLE_DRIVE_HELP_SHOWN = 'googleDriveHelpShown',
  LAST_SEEN_RELEASE_POPUP_ID = 'lastSeenReleasePopupId',
  COLUMNS_PROPERTIES = 'columnsProperties',
  IS_FIRST_VISIT = 'isFirstVisit',
  IS_PINNED_LEFT_BLOCK = 'isPinnedLeftBlock',
  IS_SEARCH_FEATURE_VIEWED = 'isSearchFeatureViewed',
  NOTES_LIST_VIEW_TYPE = 'notesListViewType',
  NOTES_LIST_SORT_TYPE = 'notesListSortType',
  NOTES_LIST_SORT_DESCENDING = 'notesListSortDescending',
  EDITOR_MARKDOWN_ENABLED = 'markdownEnabled',
  EDITOR_AWARENESS_ENABLED = 'awarenessEnabled',
  EMAIL_CONFIRMATION_TIMESTAMP = 'emailConfirmationTimestamp',
  RECENT_EMOJIES = 'recentEmojies',
  EVERNOTE_IMPORTS_COUNT = 'evernoteImportsCount',
  EDITOR_FOCUS_MODE_NOTES = 'editorFocusModeNotes',
  NOTIFICATIONS_ENABLED = 'notificationsEnabled',
  SHOW_RESOLVED_NOTE_TEXT_THREADS = 'showResolvedNoteTextThreads',
  SHOW_TASKS_LIST_MOBILE_NOTIFY = 'showTasksListMobileNotify',
  IFRAME_TEMPLATE_CLOSED = 'iframeTemplateClosed',
  IGNORE_USERS_CHECKLIST = 'ignoreUsersChecklist',
  HAS_FIRST_IMPORT = 'hasFirstImport',
  SHARE_SETTINGS_OPENED = 'shareSettingsOpened',
  SHARE_ANALYTICS_SHOWN = 'shareAnalyticsShown',
  IS_SUBSCRIPTION_SELECTED = 'isSubscriptionSelected',
  SELECTED_SUBSCRIPTION = 'selectedSubscription',
  IS_SUBSCRIPTION_CONFIRMED = 'isSubscriptionConfirmed',
  IS_ONBOARDING_SHOWN = 'isOnboardingShown',
  EDITOR_GRAMMARLY_ENABLED = 'isGrammarlyEnabled',
  DATE_TIME_LOCALE = 'dateTimeLocale',
  USE_OLD_CLIENT = 'useOldClient',
  LAST_OPENED_ORG = 'lastOpenedOrg',
  NOTES_SORT = 'notesSort',
  BOOK_DEMO_STATUS = 'bookDemoStatus',
  SIDEBAR_WIDTH = 'sidebarWidth',
  SIDEBAR_COLLAPSED = 'sidebarCollapsed',
  SIDEBAR_PORTAL_SETTINGS_COLLAPSED = 'sidebarPortalSettingsCollapsed',
  DASHBOARD_PAGE_WIDGET_WS = 'dashboardPageWidgetWs',
  FROM_CALL = 'from_call',
  IS_DASHBOARD_FIRST_VISIT = 'isDashboardFirstVisit',
  SHOW_MIGRATION_POPUP = 'showMigrationPopup',
  FIRST_VISIT_WS_NO_PAYWALL = 'firstVisitWsNoPaywall',
  TRANSCRIPT_AD_SHOWN = 'transcriptAdShown',
  SHOW_TRIAL_START_POPUP = 'showTrialStartPopup',
  STARTUP_FLOW_OPTION = 'startupFlowOption',
}

export interface GetUsersPayload extends ServicePayload {
  userIds: number[];
}
export const getUsers = async ({ host, userId, userIds }: GetUsersPayload): Promise<UserServiceUserDto[]> => {
  const chunkSize = 100;
  const chunks: number[][] = [];
  for (let i = 0; i < userIds.length; i += chunkSize) {
    chunks.push(userIds.slice(i, i + chunkSize));
  }

  const responses = await Promise.all(
    chunks.map(async (chunk) => {
      const response = await axiosClient.get(
        `${assertServiceHost(host)}/v1/users?filter=${encodeURIComponent(JSON.stringify({ id: chunk }))}`,
        { headers: { userId } }
      );
      return response.data;
    })
  );

  return responses.flat();
};

interface GetUser extends ServicePayload {
  requestedUserId?: number;
}

export interface GetUserVarPayload extends ServicePayload {
  key: USER_VARIABLE;
}

export interface SetUserVarPayload extends ServicePayload, UserVar {}

export interface SetUserSurveyPayload extends ServicePayload {
  activity: string;
  quiz_type: string;
  companySize: string;
  position: string;
  tools: string;
  solvable_tasks: string;
  services?: string;
}

export interface UpdateUserPayload extends ServicePayload {
  firstname: string;
  lastname: string;
}

export const getUser = async ({ host, userId, requestedUserId }: GetUser): Promise<UserServiceUserDto> => {
  const response = await axiosClient.get(
    `${assertServiceHost(host)}/v1/users/${requestedUserId ? requestedUserId : userId}`,
    { userId }
  );
  return response.data;
};

export const getUsersAvatars = async ({ host, userId, userIds }: GetUsersPayload): Promise<UserServiceAvatarDto[]> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/avatars?ids=${userIds.join(',')}`, { userId });
  return response.data;
};

export const getUserAvatar = async ({ host, userId }: ServicePayload): Promise<UserServiceAvatarDto | null> => {
  try {
    const response = await axiosClient.get(`${assertServiceHost(host)}/v1/avatars/${userId}`, { userId });
    return response.data;
  } catch (ex) {
    return null;
  }
};

export const getUserVars = async ({ host, userId }: ServicePayload): Promise<UserVar[]> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/users/${userId}/vars`, { userId });
  return response.data;
};

export const getUserVar = async ({ host, userId, key }: GetUserVarPayload): Promise<UserVar> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/users/${userId}/vars/${key}`, { userId });
  return response.data as UserVar;
};

export const setUserVar = async ({ host, userId, key, value }: SetUserVarPayload): Promise<UserVar> => {
  const response = await axiosClient.post(
    `${assertServiceHost(host)}/v1/users/${userId}/vars/${key}`,
    { value },
    { userId }
  );
  return response.data as UserVar;
};

export const setUserSurvey = async ({ host, userId, ...restProps }: SetUserSurveyPayload) => {
  const response = await axiosClient.post(
    `${assertServiceHost(host)}/v1/users/${userId}/activity`,
    {
      ...restProps,
    },
    {
      userId,
      headers: {
        'Content-Type': ' application/json',
      },
    }
  );
  return response.data;
};

export const updateUser = async ({ host, userId, ...restProps }: UpdateUserPayload): Promise<UserServiceUserDto> => {
  const response = await axiosClient.post(
    `${assertServiceHost(host)}/v1/users/${userId}`,
    {
      ...restProps,
    },
    {
      userId,
    }
  );
  return response.data;
};
